import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Tooltip,
  Alert,
  CircularProgress,
  Divider,
  Fade
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as ActiveIcon,
  RadioButtonUnchecked as InactiveIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { VirtualRoom } from '../services/VirtualRoomStorage';

/** The VR kinds (SC-422), as the room list shows them. */
export function kindLabel(kind: VirtualRoom['kind']): string {
  if (kind === 'cubicle') return 'Cubicle';
  if (kind === 'reception') return 'Reception';
  return 'Meeting';
}

/** By name, numbers in order ("Cubicle 2" before "Cubicle 10"), case-insensitive. */
export function sortRoomsByName(rooms: VirtualRoom[]): VirtualRoom[] {
  return [...rooms].sort((a, b) =>
    (a.name || '').localeCompare(b.name || '', undefined, { numeric: true, sensitivity: 'base' })
  );
}

interface RoomListProps {
  rooms: VirtualRoom[];
  activeRoomId: string | null;
  selectedRoomId?: string | null;
  loading?: boolean;
  error?: string | null;
  onRoomSelect: (room: VirtualRoom) => void;
  onRoomEdit: (room: VirtualRoom) => void;
  onRoomDelete: (roomId: string) => void;
  onRoomCreate: () => void;
  /** Shown right under the selected VR's row, e.g. its spawn point controls. */
  renderSelectedDetails?: (room: VirtualRoom) => React.ReactNode;
}

const RoomListItem: React.FC<{
  room: VirtualRoom;
  isActive: boolean;
  isSelected: boolean;
  onSelect: () => void;
  onEdit: () => void;
  onDelete: () => void;
}> = ({ room, isActive, isSelected, onSelect, onEdit, onDelete }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <ListItem
      disablePadding
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      sx={{
        mb: 0.5,
        borderRadius: 1,
        backgroundColor: isActive ? 'action.selected' : isSelected ? 'action.hover' : 'transparent',
        '&:hover': {
          backgroundColor: 'action.hover',
        },
      }}
    >
      <ListItemButton
        onClick={onSelect}
        sx={{
          borderRadius: 1,
          py: 1,
        }}
      >
        <ListItemIcon sx={{ minWidth: 40 }}>
          <Box
            sx={{
              width: 24,
              height: 24,
              borderRadius: '50%',
              backgroundColor: room.color,
              border: '2px solid',
              borderColor: isActive ? 'primary.main' : 'divider',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {isActive && (
              <ActiveIcon 
                sx={{ 
                  fontSize: 16, 
                  color: 'white',
                  filter: 'drop-shadow(0 0 2px rgba(0,0,0,0.5))'
                }} 
              />
            )}
          </Box>
        </ListItemIcon>

        <ListItemText
          primary={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: isActive ? 600 : isSelected ? 500 : 400,
                  color: isActive ? 'primary.main' : isSelected ? 'text.secondary' : 'text.primary',
                }}
              >
                {room.name}
              </Typography>
              {isActive && (
                <Chip
                  label="Active"
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{ height: 20, fontSize: '0.7rem' }}
                />
              )}
              {isSelected && !isActive && (
                <Chip
                  label="Selected"
                  size="small"
                  color="default"
                  variant="outlined"
                  sx={{ height: 20, fontSize: '0.7rem' }}
                />
              )}
              <Chip
                label={kindLabel(room.kind)}
                size="small"
                variant="outlined"
                data-testid={`room-kind-${room.id}`}
                sx={{ height: 20, fontSize: '0.7rem' }}
              />
              {!room.metadata?.spawnPoint && (
                <Tooltip title="Place a spawn point so people can teleport to this room">
                  <Chip
                    label="No spawn point"
                    size="small"
                    color="warning"
                    variant="outlined"
                    data-testid={`room-no-spawn-${room.id}`}
                    sx={{ height: 20, fontSize: '0.7rem' }}
                  />
                </Tooltip>
              )}
            </Box>
          }
          secondary={
            <Typography variant="caption" color="text.secondary">
              Created {new Date(room.createdAt).toLocaleDateString()}
            </Typography>
          }
        />

        <ListItemSecondaryAction>
          <Fade in={isHovered || isActive}>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <Tooltip title="Edit room">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit();
                  }}
                  sx={{
                    color: 'action.active',
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              <Tooltip title="Delete room">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete();
                  }}
                  sx={{
                    color: 'error.main',
                    '&:hover': {
                      backgroundColor: 'error.light',
                      color: 'error.contrastText',
                    },
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Fade>
        </ListItemSecondaryAction>
      </ListItemButton>
    </ListItem>
  );
};

export const RoomList: React.FC<RoomListProps> = ({
  rooms,
  activeRoomId,
  selectedRoomId = null,
  loading = false,
  error = null,
  onRoomSelect,
  onRoomEdit,
  onRoomDelete,
  onRoomCreate,
  renderSelectedDetails,
}) => {
  const [deletingRoomId, setDeletingRoomId] = useState<string | null>(null);
  const sortedRooms = useMemo(() => sortRoomsByName(rooms), [rooms]);

  const handleDelete = async (roomId: string) => {
    setDeletingRoomId(roomId);
    try {
      await onRoomDelete(roomId);
    } finally {
      setDeletingRoomId(null);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        mb: 2 
      }}>
        <Typography variant="subtitle2" color="text.secondary">
          Rooms ({rooms.length})
        </Typography>
        <Tooltip title="Create new room">
          <IconButton
            size="small"
            onClick={onRoomCreate}
            sx={{
              color: 'primary.main',
              '&:hover': {
                backgroundColor: 'primary.light',
                color: 'primary.contrastText',
              },
            }}
          >
            <AddIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <Divider sx={{ mb: 2 }} />

      {/* Room List */}
      {rooms.length === 0 ? (
        <Box sx={{ 
          textAlign: 'center', 
          py: 4,
          color: 'text.secondary'
        }}>
          <Typography variant="body2" gutterBottom>
            No rooms created yet
          </Typography>
          <Typography variant="caption">
            Click the + button to create your first room
          </Typography>
        </Box>
      ) : (
        <List sx={{ p: 0 }}>
          {sortedRooms.map((room) => (
            <React.Fragment key={room.id}>
              <RoomListItem
                room={room}
                isActive={room.id === activeRoomId}
                isSelected={room.id === selectedRoomId}
                onSelect={() => onRoomSelect(room)}
                onEdit={() => onRoomEdit(room)}
                onDelete={() => handleDelete(room.id)}
              />
              {room.id === selectedRoomId && renderSelectedDetails && (
                <ListItem
                  disablePadding
                  data-testid={`room-details-${room.id}`}
                  sx={{ display: 'block', pl: 2, pr: 1, pb: 1.5, mb: 0.5 }}
                >
                  {renderSelectedDetails(room)}
                </ListItem>
              )}
            </React.Fragment>
          ))}
        </List>
      )}

      {/* Loading overlay for delete operation */}
      {deletingRoomId && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1,
          }}
        >
          <CircularProgress size={24} />
        </Box>
      )}
    </Box>
  );
}; 